import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { Category, Product } from '@nhanho/shared'
import { formatVND, STOCK_LABEL } from '@nhanho/shared'
import { products as api, categories as catApi, explainError, type BulkAction } from '../../lib/api'
import { useList } from '../../lib/useList'
import { useDebounced } from '../../lib/useDebounced'
import { useToast } from '../../lib/toast'
import { PageHeader } from '../../components/ui/PageHeader'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Toggle } from '../../components/ui/Toggle'
import { Pagination } from '../../components/ui/Pagination'
import { Spinner } from '../../components/ui/Spinner'
import { EmptyState } from '../../components/ui/EmptyState'
import { ConfirmDialog } from '../../components/ui/Modal'
import { BadgeMask } from '../../components/product/BadgeMask'
import { LOW_STOCK } from './VariantsEditor'

const STOCK_TONE = { in_stock: 'success', pre_order: 'warning', out_of_stock: 'danger' } as const

/** Bộ lọc nhãn — mỗi option là một query flag mà API hỗ trợ. */
const FLAGS = [
  { k: 'featured', label: 'Nổi bật trang chủ' },
  { k: 'isHot', label: 'Nhãn HOT' },
  { k: 'isNewArrival', label: 'Nhãn MỚI' },
  { k: 'onSale', label: 'Đang giảm giá' },
  { k: 'lowStock', label: `Tồn thấp (≤ ${LOW_STOCK})` },
] as const
type FlagKey = (typeof FLAGS)[number]['k']

export default function ProductList() {
  /**
   * Cố định (`published: 'all'`, `populate`, `limit`) đặt TRƯỚC `...p` để URL param
   * (dropdown Đã đăng/Nháp) ghi đè được — trước đây viết ngược nên bộ lọc đó chết.
   */
  const { data, loading, params, set, refresh } = useList<Product>((p) => api.list({ published: 'all', populate: 1, limit: 20, ...p }))
  const navigate = useNavigate()
  const [cats, setCats] = useState<Category[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [confirmDel, setConfirmDel] = useState(false)
  const [busy, setBusy] = useState(false)
  const [rowBusy, setRowBusy] = useState<string | null>(null)
  const toast = useToast()

  // Tìm kiếm: gõ xong 300ms mới đẩy vào URL (= mới gọi API).
  const [q, setQ] = useState(String(params.search ?? ''))
  const [tag, setTag] = useState(String(params.tag ?? ''))
  const dq = useDebounced(q, 300)
  const dtag = useDebounced(tag, 300)
  useEffect(() => { if (dq !== String(params.search ?? '')) set({ search: dq }) }, [dq]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (dtag !== String(params.tag ?? '')) set({ tag: dtag }) }, [dtag]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { catApi.list({ limit: 500, sort: 'name' }).then((r) => setCats(r.docs)).catch((e) => toast.error(explainError(e).message)) }, [toast])
  useEffect(() => setSelected(new Set()), [data])

  const allChecked = !!data?.docs.length && data.docs.every((d) => selected.has(d.id))
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(data?.docs.map((d) => d.id)))
  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })

  const activeFlag = FLAGS.find((f) => params[f.k] === '1')?.k ?? ''
  const setFlag = (k: FlagKey | '') => set(Object.fromEntries(FLAGS.map((f) => [f.k, f.k === k ? '1' : null])))

  const bulk = async (action: BulkAction) => {
    if (!selected.size) return
    if (action === 'delete' && !confirmDel) return setConfirmDel(true)
    setBusy(true)
    try {
      const r = await api.bulk([...selected], action)
      toast.success(`Đã ${action === 'delete' ? 'xoá' : 'cập nhật'} ${r.affected} sản phẩm`)
      setConfirmDel(false); refresh()
    } catch (e) { toast.error(explainError(e).message) } finally { setBusy(false) }
  }

  /** Bật/tắt hiển thị từng dòng — không cần mở form. */
  const quickPublish = async (p: Product, published: boolean) => {
    setRowBusy(p.id)
    try { await api.update(p.id, { published }); toast.success(published ? `"${p.title}" đang hiển thị trên web` : `Đã ẩn "${p.title}"`); refresh() }
    catch (e) { toast.error(explainError(e).message) } finally { setRowBusy(null) }
  }
  const duplicate = async (p: Product) => {
    setRowBusy(p.id)
    try { const copy = await api.duplicate(p.id); toast.success('Đã tạo bản sao (nháp) — đang mở'); navigate(`/products/${copy.id}`) }
    catch (e) { toast.error(explainError(e).message); setRowBusy(null) }
  }

  const stock = (p: Product) => {
    const activeVariants = p.variants?.filter((v) => v.active) ?? []
    if (activeVariants.length) {
      const total = activeVariants.reduce((s, v) => s + (v.stockQuantity ?? 0), 0)
      const low = activeVariants.some((v) => (v.stockQuantity ?? 0) <= LOW_STOCK)
      return { text: `${activeVariants.length} biến thể · tồn ${total}`, low }
    }
    const low = p.stockType === 'in_stock' && p.stockQuantity != null && p.stockQuantity <= LOW_STOCK
    return { text: p.stockType === 'in_stock' && p.stockQuantity != null ? `${STOCK_LABEL[p.stockType]} · ${p.stockQuantity}` : STOCK_LABEL[p.stockType], low }
  }
  const price = (p: Product) => {
    const actives = (p.variants ?? []).filter((v) => v.active)
    const base = actives.length ? actives.map((v) => v.price) : [p.price]
    const sale = p.onSale ? (actives.length ? actives.map((v) => v.salePrice ?? v.price) : [p.salePrice ?? p.price]) : null
    const range = (xs: number[]) => { const a = Math.min(...xs), b = Math.max(...xs); return a === b ? formatVND(a) : `${formatVND(a)} – ${formatVND(b)}` }
    return { now: range(sale ?? base), was: sale ? range(base) : p.compareAtPrice ? formatVND(p.compareAtPrice) : null }
  }

  return (
    <>
      <PageHeader title="Sản phẩm" subtitle={data ? `${data.totalDocs} sản phẩm` : undefined} actions={<Link to="/products/new"><Button>+ Sản phẩm mới</Button></Link>} />

      <div className="mb-4 grid gap-2 md:grid-cols-[1fr_140px_170px_150px_170px_130px_150px]">
        <Input placeholder="Tìm tên, slug, SKU…" value={q} onChange={(e) => setQ(e.target.value)} />
        <Input placeholder="Tag" value={tag} onChange={(e) => setTag(e.target.value)} />
        <Select value={String(params.category ?? '')} onChange={(e) => set({ category: e.target.value })}>
          <option value="">Mọi danh mục</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        <Select value={String(params.stockType ?? '')} onChange={(e) => set({ stockType: e.target.value })}>
          <option value="">Mọi tình trạng</option>{Object.entries(STOCK_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Select>
        <Select value={activeFlag} onChange={(e) => setFlag(e.target.value as FlagKey | '')}>
          <option value="">Mọi nhãn</option>{FLAGS.map((f) => <option key={f.k} value={f.k}>{f.label}</option>)}
        </Select>
        <Select value={String(params.published ?? 'all')} onChange={(e) => set({ published: e.target.value === 'all' ? null : e.target.value })}>
          <option value="all">Hiện + ẩn</option><option value="true">Đang hiển thị</option><option value="false">Đang ẩn</option>
        </Select>
        <Select value={String(params.sort ?? '-createdAt')} onChange={(e) => set({ sort: e.target.value })}>
          <option value="-createdAt">Mới nhất</option><option value="-updatedAt">Vừa sửa</option><option value="priority">Ưu tiên (như web)</option><option value="order">Thứ tự tay</option><option value="title">Tên A→Z</option><option value="price">Giá tăng</option><option value="-price">Giá giảm</option>
        </Select>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-button bg-primary-100 px-3 py-2 text-sm">
          <span className="font-medium text-ink">{selected.size} đã chọn</span>
          <Button size="sm" variant="secondary" loading={busy} onClick={() => bulk('publish')}>Hiển thị</Button>
          <Button size="sm" variant="secondary" loading={busy} onClick={() => bulk('unpublish')}>Ẩn</Button>
          <span className="mx-1 h-5 w-px bg-border-strong" />
          <Button size="sm" variant="secondary" loading={busy} onClick={() => bulk('feature')}>★ Nổi bật</Button>
          <Button size="sm" variant="secondary" loading={busy} onClick={() => bulk('unfeature')}>Bỏ nổi bật</Button>
          <Button size="sm" variant="secondary" loading={busy} onClick={() => bulk('hot')}>HOT</Button>
          <Button size="sm" variant="secondary" loading={busy} onClick={() => bulk('unhot')}>Bỏ HOT</Button>
          <Button size="sm" variant="secondary" loading={busy} onClick={() => bulk('new')}>MỚI</Button>
          <Button size="sm" variant="secondary" loading={busy} onClick={() => bulk('unnew')}>Bỏ MỚI</Button>
          <span className="mx-1 h-5 w-px bg-border-strong" />
          <Button size="sm" variant="danger" loading={busy} onClick={() => bulk('delete')}>Xoá</Button>
        </div>
      )}

      {loading && !data ? <Spinner /> : !data?.docs.length ? (
        <EmptyState title="Không có sản phẩm nào" hint={params.search || activeFlag ? 'Thử bỏ bộ lọc hoặc từ khoá khác.' : 'Tạo sản phẩm đầu tiên.'} action={<Link to="/products/new"><Button>+ Sản phẩm mới</Button></Link>} />
      ) : (
        <div className="overflow-x-auto rounded-card border border-border bg-white shadow-card">
          <table className="w-full text-sm">
            <thead className="bg-sand text-left text-xs uppercase tracking-wider text-ink-muted">
              <tr>
                <th className="w-10 px-3 py-2.5"><input type="checkbox" checked={allChecked} onChange={toggleAll} aria-label="Chọn tất cả" /></th>
                <th className="px-3 py-2.5">Sản phẩm</th><th className="px-3 py-2.5">SKU</th><th className="px-3 py-2.5">Giá</th>
                <th className="px-3 py-2.5">Tồn kho</th><th className="px-3 py-2.5">Danh mục</th><th className="px-3 py-2.5">Thứ tự</th>
                <th className="px-3 py-2.5">Trên web</th><th className="px-3 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.docs.map((p) => {
                const img = typeof p.images?.[0] === 'object' ? p.images[0] : null
                const cat = typeof p.category === 'object' ? p.category : null
                const st = stock(p)
                const pr = price(p)
                return (
                  <tr key={p.id} className={selected.has(p.id) ? 'bg-primary-50' : 'hover:bg-sand/60'}>
                    <td className="px-3 py-2"><input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} aria-label={`Chọn ${p.title}`} /></td>
                    <td className="px-3 py-2">
                      <Link to={`/products/${p.id}`} className="flex items-center gap-3">
                        <div className="relative size-12 shrink-0 overflow-hidden rounded-image bg-sand">
                          {img ? <img src={img.sizes?.thumbnail?.url ?? img.url} alt="" className="size-full object-cover" /> : <div className="grid size-full place-items-center text-xs text-ink-faint">no img</div>}
                          <BadgeMask product={p} />
                        </div>
                        <div className="min-w-0">
                          <div className="truncate font-medium text-ink hover:underline">{p.title}</div>
                          <div className="truncate text-xs text-ink-muted">/{p.slug}{p.featured && <span className="ml-2 text-warning">★ nổi bật</span>}</div>
                        </div>
                      </Link>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-ink-muted">{p.sku || '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className={p.onSale ? 'font-medium text-warning' : ''}>{pr.now}</div>
                      {pr.was && <div className="text-xs text-ink-faint line-through">{pr.was}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <Badge tone={st.low ? 'warning' : STOCK_TONE[p.stockType]}>{st.text}</Badge>
                      {st.low && <div className="mt-0.5 text-2xs text-warning">tồn thấp</div>}
                    </td>
                    <td className="px-3 py-2 text-ink-muted">{cat?.name ?? '—'}</td>
                    <td className="px-3 py-2 text-ink-muted">{p.order ?? 0}</td>
                    <td className="px-3 py-2">
                      <Toggle checked={p.published} disabled={rowBusy === p.id} onChange={(v) => void quickPublish(p, v)} label={p.published ? 'Hiện' : 'Ẩn'} />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      <Button variant="ghost" size="sm" loading={rowBusy === p.id} onClick={() => void duplicate(p)} title="Tạo bản sao (nháp)">Nhân bản</Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <div className="border-t border-border px-3 py-2.5"><Pagination data={data} onPage={(p) => set({ page: p })} /></div>
        </div>
      )}
      <ConfirmDialog open={confirmDel} onCancel={() => setConfirmDel(false)} onConfirm={() => bulk('delete')} loading={busy} title="Xoá sản phẩm" message={<>Xoá <strong>{selected.size}</strong> sản phẩm đã chọn? Không khôi phục được.</>} />
    </>
  )
}
